import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, TextField, FormControlLabel, Checkbox, Button, Chip, Collapse, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel, TablePagination, MenuItem,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { useIsCloudAdmin } from '../context/PermissionsContext';
import { saveBlob, downloadError } from '../utils/download';
import ReportingAllocations from '../components/ReportingAllocations';
import { PERIODS, errText } from '../components/ReportingUsageParts';

const PER_PAGE = 25;
const FILE_PERIOD = { last_month: 'Last_Month', last_year: 'Last_12_Months', all_time: 'All_Time' };

// Customers with their licence (bundle, top-ups, limits). Customers themselves are created and edited on the portal's own Customers screen.
// Props: onOpenCustomer(code) (optional) adds an "Open overview" button for the page that shows one customer.
export default function ReportingCustomers({ onOpenCustomer }) {
  const canEdit = useIsCloudAdmin();
  const [customers, setCustomers] = useState(null);
  const [error, setError] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ by: 'cust_name', dir: 'asc' });
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(null);
  const [exportPeriod, setExportPeriod] = useState('last_year');

  useEffect(() => {
    let live = true;
    setCustomers(null);
    reportingUsageAPI.customers(activeOnly)
      .then((res) => { if (live) { setCustomers(res.data || []); setError(''); } })
      .catch((err) => { if (live) { setCustomers([]); setError(errText(err, 'Could not load customers')); } });
    return () => { live = false; };
  }, [activeOnly]);
  useEffect(() => { setPage(0); setOpen(null); }, [search, activeOnly, sort]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (c) => (sort.by === 'is_active' ? (c.is_active ? 1 : 0) : String(c[sort.by] ?? '').toLowerCase());
    return (customers || [])
      .filter((c) => !q || [c.cust_code, c.cust_name, c.location, c.cl_number].some((v) => String(v || '').toLowerCase().includes(q)))
      .sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
  }, [customers, search, sort]);
  const sortBy = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === 'asc' ? 'desc' : 'asc' }));
  const head = (by, label) => (
    <TableCell sortDirection={sort.by === by ? sort.dir : false}>
      <TableSortLabel active={sort.by === by} direction={sort.by === by ? sort.dir : 'asc'} onClick={() => sortBy(by)}>{label}</TableSortLabel>
    </TableCell>
  );

  const download = async (call, filename) => {
    try { const res = await call(); saveBlob(new Blob([res.data], { type: 'text/csv' }), filename); }
    catch (err) { setError(await downloadError(err, 'CSV export failed')); }
  };

  const shown = rows.slice(page * PER_PAGE, (page + 1) * PER_PAGE);

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Customers and Licences</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each customer's bundle, top-ups and combined limits. Open a row to see the licence{canEdit ? ' and change it' : ''}. To add or edit the customer itself use the Customers screen.
      </Typography>
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField size="small" label="Search" placeholder="Name, code, location or CL number" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <FormControlLabel control={<Checkbox size="small" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />} label="Active customers only" />
        <Box sx={{ flexGrow: 1 }} />
        <TextField select size="small" label="Export period" value={exportPeriod} onChange={(e) => setExportPeriod(e.target.value)} sx={{ minWidth: 160 }}>
          {Object.entries(PERIODS).map(([k, label]) => <MenuItem key={k} value={k}>{label}</MenuItem>)}
        </TextField>
        <Button size="small" variant="outlined" startIcon={<DownloadIcon />}
          onClick={() => download(() => reportingUsageAPI.exportAllCsv(exportPeriod), `All_Customers_Sample_Test_Counts_${FILE_PERIOD[exportPeriod]}.csv`)}>Export all customers</Button>
      </Paper>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!customers && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {customers && (
        <Paper>
          <TableContainer>
            <Table size="small" aria-label="Customers">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {head('cust_code', 'Code')}{head('cust_name', 'Name')}{head('cl_number', 'CL number')}{head('location', 'Location')}{head('is_active', 'Status')}
                </TableRow>
              </TableHead>
              <TableBody>
                {shown.map((c) => {
                  const isOpen = open === c.cust_code;
                  return (
                    <React.Fragment key={c.cust_code}>
                      <TableRow hover selected={isOpen} sx={{ cursor: 'pointer', '& > td': { borderBottom: isOpen ? 'none' : undefined } }} onClick={() => setOpen(isOpen ? null : c.cust_code)}>
                        <TableCell padding="checkbox">
                          <IconButton size="small" aria-label={`${isOpen ? 'Close' : 'Open'} ${c.cust_name}`}>{isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
                        </TableCell>
                        <TableCell>{c.cust_code}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{c.cust_name}</TableCell>
                        <TableCell>{c.cl_number || '-'}</TableCell>
                        <TableCell>{c.location || '-'}</TableCell>
                        <TableCell><Chip size="small" color={c.is_active ? 'success' : 'default'} label={c.is_active ? 'Active' : 'Inactive'} /></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderBottom: isOpen ? undefined : 'none' }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ p: 2, bgcolor: 'action.hover' }}>
                              <Box sx={{ display: 'flex', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                {onOpenCustomer && <Button size="small" variant="outlined" onClick={() => onOpenCustomer(c.cust_code)}>Open overview</Button>}
                                <Button size="small" startIcon={<DownloadIcon />} onClick={() => download(() => reportingUsageAPI.exportCustomerCsv(c.cust_code), `${c.cust_code.toUpperCase()}_profile_export.csv`)}>Export sample and test counts</Button>
                              </Box>
                              <ReportingAllocations custCode={c.cust_code} canEdit={canEdit} />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <TableRow><TableCell colSpan={6}>No customers match.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={rows.length} page={page} onPageChange={(e, p) => setPage(p)} rowsPerPage={PER_PAGE} rowsPerPageOptions={[PER_PAGE]} />
        </Paper>
      )}
    </Box>
  );
}
